import { ACCOUNT_ICON_SRC } from '../assets/account-icons/index.js';
import { createPlayerI18n } from '../i18n/language.js';
import { fetchVipSnapshot } from '../vip/helpers.js';
import { isAccountAbortError } from '../boot/accountCatalogCache.js';
import vipCardBg from './personal-assets/vip-card-bg-1.jpg';
import profileHeaderBg from './personal-assets/bg-header-profile.jpg';
import memberPic from './personal-assets/memberpic-lv1.svg?no-inline';
import iconUsername from './personal-assets/icon-username.svg?no-inline';
import iconBirthday from './personal-assets/icon-birthday.svg?no-inline';
import iconEmail from './personal-assets/icon-email.svg?no-inline';
import iconPhone from './personal-assets/icon-phone.svg?no-inline';
import {
  OTP_DIGITS,
  PHONE_DIGITS,
  PRIVACY_NOTICE,
  digitsOnly,
  fetchPlayerProfile,
  fetchPlayerWallet,
  formatBdPhoneDisplay,
  formatVipPoints,
  formatYmd,
  hasVipCoinValue,
  normalizePhoneForApi,
  patchProfileField,
  patchProfilePhone,
  pickUsername,
  pickVipRankName,
  postProfilePhone,
  primaryPhone,
  realEmails,
  sendProfileOtp,
  sessionUser,
  validatePhoneInput,
  verifyProfileOtp,
} from './personal-info-helpers.js';

const COPY = {
  fullName: 'Full Name',
  birthday: 'Birthday',
  phone: 'Phone Number',
  email: 'Email',
  registered: 'Date Registered',
  vipPoints: 'VIP Points (VP)',
  myVip: 'My VIP',
  default: 'Default',
  verified: 'Verified',
  notVerified: 'Not Verified',
  add: 'Add',
  addFullName: 'Add Full Name',
  addBirthday: 'Add Birthday',
  addEmail: 'Add E-mail',
  addPhone: 'Add Phone Number',
  verifyPhone: 'Verify Phone Number',
  fullNamePlaceholder: 'Your full name ...',
  emailPlaceholder: 'Enter your E-mail.',
  phonePlaceholder: 'Enter your phone number.',
  phoneHint: '11 digit phone number',
  save: 'Add',
  cancel: 'Cancel',
  verify: 'Verify',
  change: 'Change',
  otp: 'Please input verification code',
  otpConfirm: 'Confirm',
  otpResend: 'Resend',
  verifyPrimaryFirst: 'Verify your primary phone before adding another number.',
  confirmPrimaryToAdd: 'Confirm your primary phone with OTP before adding another number.',
  maxContacts: 'Maximum 2 allowed.',
  otpExpired: 'Code expired. Request a new one.',
  otpWrong: 'Incorrect code.',
  fieldLocked: 'Cannot be changed',
  notSet: 'Not Set',
  empty: '—',
  emailPending: 'Email save will be linked when the email API is available.',
};

const SCREENS = {
  main: 'main',
  fullName: 'fullName',
  birthday: 'birthday',
  email: 'email',
  phone: 'phone',
  changePhone: 'changePhone',
  verify: 'verify',
};

export function createPersonalInfoPage({ useState, useEffect, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  function CloseIcon() {
    return jsxs('svg', {
      className: 'pi-icon',
      viewBox: '0 0 24 24',
      'aria-hidden': 'true',
      children: [
        jsx('path', {
          d: 'M6 6l12 12M18 6L6 18',
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: '1.8',
          strokeLinecap: 'round',
        }),
      ],
    });
  }

  function FieldIcon({ src, label }) {
    return jsx('span', {
      className: 'pi-icon-block',
      children:         jsx('span', {
        className: 'pi-field-icon',
        role: 'img',
        'aria-label': label,
        style: { '--pi-mask': `url("${src}")` },
      }),
    });
  }

  function PersonalInfoPage({ t, onClose, onVip }) {
    const { lang, phrase } = usePlayerI18n();
    const [profile, setProfile] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [vip, setVip] = useState(null);
    const [screen, setScreen] = useState(SCREENS.main);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [otpMode, setOtpMode] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [phoneDraft, setPhoneDraft] = useState('');
    const [changeId, setChangeId] = useState(null);
    const [emailNote, setEmailNote] = useState('');

    const load = async () => {
      const next = await fetchPlayerProfile();
      setProfile(next || {});
      return next || {};
    };

    useEffect(() => {
      let cancelled = false;
      Promise.all([
        fetchPlayerProfile().catch(() => ({})),
        fetchPlayerWallet().catch(() => null),
        fetchVipSnapshot().catch((err) => {
          if (isAccountAbortError(err)) return null;
          return null;
        }),
      ]).then(([nextProfile, nextWallet, nextVip]) => {
        if (cancelled) return;
        setProfile(nextProfile || {});
        setWallet(nextWallet);
        setVip(nextVip);
      });
      return () => {
        cancelled = true;
      };
    }, []);

    const close = () => {
      if (typeof onClose === 'function') onClose();
    };

    const openVip = () => {
      if (typeof onVip === 'function') onVip();
    };

    const username = pickUsername(profile, sessionUser());
    const rankName = pickVipRankName(vip);
    const regDate = formatYmd(profile?.regDate);
    const fullName = String(profile?.fullName || '').trim();
    const dob = formatYmd(profile?.dob);
    const phones = Array.isArray(profile?.phones) ? profile.phones : [];
    const emails = realEmails(profile?.emails);
    const mainPhone = primaryPhone(phones);
    const canAddSecondary = Boolean(profile?.canAddSecondary);
    const accountPhone = String(profile?.phone || mainPhone?.value || '');
    const showVipCoin = hasVipCoinValue(wallet);
    const vipPoints = showVipCoin ? formatVipPoints(wallet.vipCoin) : '';
    const hasVipCard = Boolean(rankName);

    const resetForms = () => {
      setScreen(SCREENS.main);
      setDraft('');
      setError('');
      setOtpMode(null);
      setOtpCode('');
      setPhoneDraft('');
      setChangeId(null);
      setEmailNote('');
    };

    const openScreen = (next) => {
      setError('');
      setEmailNote('');
      setDraft('');
      setPhoneDraft('');
      setScreen(next);
    };

    const saveField = async (field) => {
      const value = String(draft || '').trim();
      if (!value) {
        setError(phrase(COPY.notSet));
        return;
      }
      try {
        const next = await patchProfileField(field, value);
        setProfile(next || {});
        resetForms();
      } catch (err) {
        setError(err?.message || phrase(COPY.fieldLocked));
      }
    };

    const startOtp = async (row, purpose) => {
      const destination = normalizePhoneForApi(row?.value || accountPhone);
      if (!destination) {
        setError(phrase(COPY.verifyPrimaryFirst));
        return;
      }
      try {
        await sendProfileOtp({
          channel: 'phone',
          destination,
          contactId: row?.id,
          purpose,
        });
        setError('');
        setOtpCode('');
        setOtpMode({ id: row?.id, purpose, destination });
        setScreen(SCREENS.verify);
      } catch (err) {
        setError(err?.message || phrase(COPY.notSet));
      }
    };

    const confirmOtp = async () => {
      if (!otpMode) return;
      const code = digitsOnly(otpCode, OTP_DIGITS);
      if (!code) {
        setError(phrase(COPY.otpWrong));
        return;
      }
      const result = await verifyProfileOtp({
        channel: 'phone',
        destination: otpMode.destination,
        code,
        purpose: otpMode.purpose,
        phoneId: otpMode.purpose === 'verify-phone' ? otpMode.id : undefined,
      }).then(() => ({ ok: true })).catch((err) => {
        const msg = String(err?.message || '').toLowerCase();
        return { ok: false, expired: msg.includes('expired'), message: err?.message };
      });
      if (!result.ok) {
        setError(result.expired ? phrase(COPY.otpExpired) : (result.message || phrase(COPY.otpWrong)));
        return;
      }
      if (otpMode.purpose === 'add-secondary') {
        setOtpMode(null);
        setOtpCode('');
        setError('');
        setScreen(SCREENS.phone);
        await load();
        return;
      }
      resetForms();
      await load();
    };

    const onAddPhone = async () => {
      if (phones.length >= 2) {
        setError(phrase(COPY.maxContacts));
        return;
      }
      if (!phones.length) {
        openScreen(SCREENS.phone);
        return;
      }
      if (!mainPhone?.verified) {
        setError(phrase(COPY.verifyPrimaryFirst));
        return;
      }
      if (canAddSecondary) {
        openScreen(SCREENS.phone);
        setError('');
        return;
      }
      await startOtp(mainPhone, 'add-secondary');
    };

    const saveNewPhone = async () => {
      const check = validatePhoneInput(phoneDraft);
      if (!check.ok) {
        setError(phrase(COPY.phoneHint));
        return;
      }
      if (phones.some((row) => normalizePhoneForApi(row.value) === check.value)) {
        setError(phrase(COPY.notSet));
        return;
      }
      try {
        const next = await postProfilePhone(check.value);
        setProfile(next || {});
        resetForms();
      } catch (err) {
        setError(err?.message || phrase(COPY.notSet));
      }
    };

    const saveChangedPhone = async () => {
      const check = validatePhoneInput(phoneDraft);
      if (!check.ok) {
        setError(phrase(COPY.phoneHint));
        return;
      }
      try {
        const next = await patchProfilePhone(changeId, check.value);
        setProfile(next || {});
        resetForms();
      } catch (err) {
        setError(err?.message || phrase(COPY.notSet));
      }
    };

    const saveEmailVisual = () => {
      const value = String(draft || '').trim();
      if (!value || !value.includes('@')) {
        setError(phrase(COPY.emailPlaceholder));
        return;
      }
      setError('');
      setEmailNote(phrase(COPY.emailPending));
    };

    const privacyMarker = lang === 'bn' ? 'কাস্টমার সার্ভিসে যোগাযোগ করুন' : 'contact customer service';
    const privacy = phrase(PRIVACY_NOTICE).replace(privacyMarker, '{{cs}}').split('{{cs}}');

    const showAddPhoneLink = phones.length === 0
      || (phones.length < 2 && Boolean(mainPhone?.verified));

    const headerTitle = screen === SCREENS.fullName
      ? phrase(COPY.addFullName)
      : screen === SCREENS.birthday
        ? phrase(COPY.addBirthday)
        : screen === SCREENS.email
          ? phrase(COPY.addEmail)
          : screen === SCREENS.phone || screen === SCREENS.changePhone
            ? phrase(COPY.addPhone)
            : screen === SCREENS.verify
              ? phrase(COPY.verifyPhone)
              : 'Personal Info';

    const fieldRow = ({
      name,
      icon,
      value,
      tag,
      emptyAction,
      onEmpty,
      status,
      onStatus,
      extra,
    }) => jsxs('div', {
      className: 'pi-group',
      'data-pi-row': name,
      children: [
        jsx(FieldIcon, { src: icon, label: name }),
        jsxs('div', {
          className: 'pi-list-cell',
          children: [
            jsxs('div', {
              className: 'pi-list-info',
              children: [
                jsxs('div', {
                  className: 'pi-left',
                  children: [
                    jsx('div', { className: 'pi-row-title', children: name }),
                    tag ? jsx('span', { className: 'pi-tag', children: tag }) : null,
                    value
                      ? jsx('div', { className: 'pi-row-value', children: value })
                      : null,
                    extra || null,
                  ],
                }),
                jsx('div', {
                  className: 'pi-right',
                  children: !value && emptyAction
                    ? jsx('button', {
                      type: 'button',
                      className: 'pi-add-btn',
                      onClick: onEmpty,
                      children: phrase(COPY.add),
                    })
                    : status
                      ? (
                        onStatus
                          ? jsx('button', {
                            type: 'button',
                            className: `pi-status${status === phrase(COPY.verified) ? '' : ' is-unverified'}`,
                            onClick: onStatus,
                            children: status,
                          })
                          : jsx('span', {
                            className: `pi-status${status === phrase(COPY.verified) ? '' : ' is-unverified'}`,
                            children: status,
                          })
                      )
                      : null,
                }),
              ],
            }),
          ],
        }),
      ],
    });

    const formScreen = (fields) => jsxs('div', {
      className: 'pi-form-screen',
      children: [
        fields,
        error ? jsx('p', { className: 'pi-error', children: error }) : null,
      ],
    });

    const inputBlock = ({
      label,
      type,
      value,
      onChange,
      placeholder,
      maxLength,
      inputMode,
    }) => jsxs('label', {
      className: 'pi-input-group',
      children: [
        jsx('span', { children: label }),
        jsx('input', {
          className: 'pi-input',
          type,
          inputMode,
          maxLength,
          value,
          placeholder,
          autoFocus: true,
          onChange,
        }),
      ],
    });

    return jsxs('div', {
      className: 'pi-page',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Personal Info',
      children: [
        jsxs('header', {
          className: 'pi-header',
          children: [
            screen === SCREENS.main
              ? null
              : jsx('button', {
                type: 'button',
                className: 'pi-sub-back',
                'aria-label': 'Back',
                onClick: resetForms,
                children: jsx('span', { className: 'pi-sub-back-icon', 'aria-hidden': 'true' }),
              }),
            screen === SCREENS.main
              ? jsx('h1', { className: 'pi-title', children: 'Personal Info' })
              : jsx('h1', { className: 'pi-title', children: headerTitle }),
            screen === SCREENS.main
              ? jsx('button', {
                type: 'button',
                className: 'pi-close',
                'aria-label': 'Close',
                onClick: close,
                children: jsx(CloseIcon, {}),
              })
              : null,
          ],
        }),
        screen === SCREENS.main
          ? jsxs('div', {
            className: 'pi-body',
            children: [
              jsxs('section', {
                className: `pi-vip${hasVipCard ? ' is-vip' : ''}`,
                children: [
                  jsx('div', {
                    className: hasVipCard ? 'pi-card is-vip' : 'pi-card',
                    style: {
                      backgroundImage: `url(${hasVipCard ? vipCardBg : profileHeaderBg})`,
                    },
                    children: jsx('div', {
                      className: 'pi-avatar',
                      children: jsx('img', {
                        className: 'pi-avatar-img',
                        src: memberPic || ACCOUNT_ICON_SRC.memberPic,
                        alt: '',
                      }),
                    }),
                  }),
                  jsxs('div', {
                    className: 'pi-namewrap',
                    children: [
                      jsxs('div', {
                        className: 'pi-nameblock',
                        children: [
                          jsx('p', {
                            className: 'pi-username',
                            'data-pi-username': username,
                            children: username || phrase(COPY.empty),
                          }),
                          rankName
                            ? jsx('span', {
                              className: 'pi-level',
                              'data-pi-vip-level': rankName,
                              children: rankName,
                            })
                            : null,
                        ],
                      }),
                      jsx('p', {
                        className: 'pi-reg',
                        'data-pi-reg': regDate || '',
                        children: `${phrase(COPY.registered)} : ${regDate || phrase(COPY.empty)}`,
                      }),
                    ],
                  }),
                ],
              }),
              jsx('section', {
                className: 'pi-menu pi-vip-menu',
                id: 'profile-vip-div',
                children: jsxs('div', {
                  className: 'pi-vip-area',
                  children: [
                    jsxs('div', {
                      className: 'pi-vip-left',
                      children: [
                        jsx('h3', { children: phrase(COPY.vipPoints) }),
                        jsx('span', {
                          className: 'pi-vp-value',
                          'data-pi-vip-points': vipPoints,
                          children: vipPoints || phrase(COPY.empty),
                        }),
                      ],
                    }),
                    jsx('button', {
                      type: 'button',
                      className: 'pi-my-vip',
                      onClick: openVip,
                      children: jsxs('span', {
                        className: 'pi-my-vip-text',
                        children: [
                          phrase(COPY.myVip),
                          jsx('i', { 'aria-hidden': 'true' }),
                        ],
                      }),
                    }),
                  ],
                }),
              }),
              jsxs('section', {
                className: 'pi-menu',
                children: [
                  fieldRow({
                    name: phrase(COPY.fullName),
                    icon: iconUsername,
                    value: fullName,
                    emptyAction: true,
                    onEmpty: () => openScreen(SCREENS.fullName),
                  }),
                  fieldRow({
                    name: phrase(COPY.birthday),
                    icon: iconBirthday,
                    value: dob,
                    emptyAction: true,
                    onEmpty: () => openScreen(SCREENS.birthday),
                  }),
                  phones.length
                    ? phones.map((row) => fieldRow({
                      name: phrase(COPY.phone),
                      icon: iconPhone,
                      value: formatBdPhoneDisplay(row.value) || row.value,
                      tag: row.isPrimary ? phrase(COPY.default) : '',
                      status: row.verified ? phrase(COPY.verified) : phrase(COPY.notVerified),
                      onStatus: row.verified ? null : () => startOtp(row, 'verify-phone'),
                      extra: !row.isPrimary
                        ? jsx('button', {
                          type: 'button',
                          className: 'pi-change',
                          onClick: () => {
                            setChangeId(row.id);
                            setPhoneDraft('');
                            setError('');
                            setScreen(SCREENS.changePhone);
                          },
                          children: phrase(COPY.change),
                        })
                        : null,
                    }))
                    : fieldRow({
                      name: phrase(COPY.phone),
                      icon: iconPhone,
                      value: '',
                      emptyAction: true,
                      onEmpty: onAddPhone,
                    }),
                  showAddPhoneLink
                    ? jsx('button', {
                      type: 'button',
                      className: 'pi-add-link',
                      onClick: onAddPhone,
                      children: jsxs('span', {
                        children: [
                          jsx('span', { className: 'pi-add-link-icon', 'aria-hidden': 'true' }),
                          phrase(COPY.add),
                        ],
                      }),
                    })
                    : null,
                  emails.length
                    ? emails.map((row) => fieldRow({
                      name: phrase(COPY.email),
                      icon: iconEmail,
                      value: row.value,
                      status: row.verified ? phrase(COPY.verified) : phrase(COPY.notVerified),
                    }))
                    : fieldRow({
                      name: phrase(COPY.email),
                      icon: iconEmail,
                      value: '',
                      emptyAction: true,
                      onEmpty: () => openScreen(SCREENS.email),
                    }),
                ],
              }),
              error ? jsx('p', { className: 'pi-error', children: error }) : null,
              jsxs('div', {
                className: 'pi-notice',
                children: [
                  jsx('span', {
                    className: 'pi-notice-icon',
                    style: { backgroundImage: `url(${ACCOUNT_ICON_SRC.talk})` },
                    'aria-hidden': 'true',
                  }),
                  jsxs('p', {
                    children: [
                      privacy[0],
                      jsx('span', { className: 'pi-notice-cs', children: 'contact customer service' }),
                      privacy[1] || '',
                    ],
                  }),
                ],
              }),
            ],
          })
          : null,
        screen === SCREENS.fullName
          ? formScreen(jsxs('div', {
            children: [
              inputBlock({
                label: phrase(COPY.fullName),
                type: 'text',
                value: draft,
                placeholder: phrase(COPY.fullNamePlaceholder),
                onChange: (e) => setDraft(e.target.value),
              }),
              jsx('button', {
                type: 'button',
                className: 'pi-submit',
                onClick: () => saveField('fullName'),
                children: phrase(COPY.add),
              }),
            ],
          }))
          : null,
        screen === SCREENS.birthday
          ? formScreen(jsxs('div', {
            children: [
              inputBlock({
                label: phrase(COPY.birthday),
                type: 'date',
                value: draft,
                onChange: (e) => setDraft(e.target.value),
              }),
              jsx('button', {
                type: 'button',
                className: 'pi-submit',
                onClick: () => saveField('dob'),
                children: phrase(COPY.add),
              }),
            ],
          }))
          : null,
        screen === SCREENS.email
          ? formScreen(jsxs('div', {
            children: [
              inputBlock({
                label: phrase(COPY.email),
                type: 'email',
                value: draft,
                placeholder: phrase(COPY.emailPlaceholder),
                onChange: (e) => setDraft(e.target.value),
              }),
              jsx('button', {
                type: 'button',
                className: 'pi-submit',
                onClick: saveEmailVisual,
                children: phrase(COPY.add),
              }),
              emailNote ? jsx('p', { className: 'pi-note', children: emailNote }) : null,
            ],
          }))
          : null,
        screen === SCREENS.phone || screen === SCREENS.changePhone
          ? formScreen(jsxs('div', {
            children: [
              inputBlock({
                label: phrase(COPY.phone),
                type: 'tel',
                inputMode: 'numeric',
                maxLength: PHONE_DIGITS,
                value: phoneDraft,
                placeholder: phrase(COPY.phonePlaceholder),
                onChange: (e) => setPhoneDraft(normalizePhoneForApi(e.target.value)),
              }),
              jsx('button', {
                type: 'button',
                className: 'pi-submit',
                onClick: screen === SCREENS.phone ? saveNewPhone : saveChangedPhone,
                children: phrase(COPY.add),
              }),
            ],
          }))
          : null,
        screen === SCREENS.verify
          ? formScreen(jsxs('div', {
            children: [
              jsx('p', {
                className: 'pi-note pi-note-block',
                children: otpMode?.purpose === 'add-secondary'
                  ? phrase(COPY.confirmPrimaryToAdd)
                  : phrase(COPY.otp),
              }),
              inputBlock({
                label: phrase(COPY.otp),
                type: 'tel',
                inputMode: 'numeric',
                maxLength: OTP_DIGITS,
                value: otpCode,
                placeholder: phrase(COPY.otp),
                onChange: (e) => setOtpCode(digitsOnly(e.target.value, OTP_DIGITS)),
              }),
              jsx('button', {
                type: 'button',
                className: 'pi-submit',
                onClick: confirmOtp,
                children: phrase(COPY.otpConfirm),
              }),
            ],
          }))
          : null,
      ],
    });
  }

  return PersonalInfoPage;
}
